import { useQuery } from "@tanstack/react-query";
import { useWorkspaceId } from "../bridges/session";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";
import type { AccessPreset, AccessRole } from "./roleLabel";

export type { AccessPreset, AccessRole } from "./roleLabel";

/* ---------- types (wire contract: plan 5A "Endpoint summary (as built)", me/access and account/basics) ---------- */

/** The permission catalogue (wire names identical to the server's `Perm`). */
export const PERMS = [
  "contacts.read", "contacts.see_details", "contacts.see_history", "contacts.add", "contacts.add_direct", "contacts.edit",
  "contacts.delete", "contacts.approve", "contacts.import", "contacts.export", "contacts.combine", "categories.manage",
  "reports.write", "reports.edit_any",
  "routes.read", "routes.follow", "routes.change", "routes.plan", "routes.delete_all", "projects.manage",
  "team.see", "team.invite", "team.managers", "ownership.transfer", "volunteers.manage",
  "account.leave", "settings.manage", "billing.view", "account.close", "alerts.receive",
] as const;
export type Perm = (typeof PERMS)[number];

type Person = { id: number; name: string };
/** The account's Owner as everyone else reads them: "your manager". `email` only comes to members of the account. */
type Contact = Person & { email: string | null };

export type MyAccess = {
  workspace: {
    id: number;
    name: string;
    /** "next": the new access model is on for this account; anything else: today's. */
    model: "next" | "legacy";
    accountType: string | null;
    teamAllowed: boolean;
    owner: Contact | null;
  };
  role: AccessRole;
  /** A Team member's starting set; null for the other roles. */
  preset: AccessPreset | null;
  changedSwitches: number;
  permissions: Perm[];
  reach: { all: boolean; categories: number; contacts: number; routes: number };
  /** Whether the Routes list would show this person at least one route (owner note A3: without Plan routes, no routes = no Routes item). Undefined from an older answer reads as "yes". */
  hasRoutes: boolean;
  endsAt: string | null;
  /** `reason` and `contactName` only come with the right to see billing. */
  viewOnly: { active: boolean; reason: string | null; contactName: string | null };
  /** While a ConnectYid staff member views the account as this person. `staffName` null reads "ConnectYid staff". */
  viewingAs: { staffName: string | null } | null;
  handover: { id: number; to: Person; from: Person; mineToAccept: boolean } | null;
};

export type AccountBasics = {
  name: string;
  countryCode: string | null;
  dateFormat: string | null;
  defaultStart: { line: string; lat: number; lng: number } | null;
  categories: { id: number; name: string; color: string | null }[];
  projects: { id: number; name: string; color: string | null; status: string }[];
  currentProjectId: number | null;
  scheduleOptions: string[];
  /** Names only, never contact details. */
  memberNames: Person[];
};

/* ---------- wire -> app ---------- */

type Wire = Record<string, any>;

const mapAccess = (w: Wire): MyAccess => ({
  workspace: {
    id: w.workspace.id,
    name: w.workspace.name,
    model: w.workspace.model === "next" ? "next" : "legacy",
    accountType: w.workspace.account_type ?? null,
    teamAllowed: w.workspace.team_allowed ?? true,
    owner: w.workspace.owner ? { id: w.workspace.owner.id, name: w.workspace.owner.name, email: w.workspace.owner.email ?? null } : null,
  },
  role: w.role,
  preset: w.preset ?? null,
  changedSwitches: w.changed_switches ?? 0,
  permissions: w.permissions ?? [],
  reach: { all: !!w.reach?.all, categories: w.reach?.categories ?? 0, contacts: w.reach?.contacts ?? 0, routes: w.reach?.routes ?? 0 },
  hasRoutes: w.has_routes !== false,
  endsAt: w.ends_at ?? null,
  viewOnly: { active: !!w.view_only?.active, reason: w.view_only?.reason ?? null, contactName: w.view_only?.contact_name ?? null },
  viewingAs: w.viewing_as ? { staffName: w.viewing_as.staff_name ?? null } : null,
  handover: w.handover ? { id: w.handover.id, to: w.handover.to, from: w.handover.from, mineToAccept: !!w.handover.mine_to_accept } : null,
});

const mapBasics = (w: Wire): AccountBasics => ({
  name: w.name,
  countryCode: w.country_code ?? null,
  dateFormat: w.date_format ?? null,
  defaultStart: w.default_start ?? null,
  categories: w.categories ?? [],
  projects: w.projects ?? [],
  currentProjectId: w.current_project_id ?? null,
  scheduleOptions: w.schedule_options ?? [],
  memberNames: w.member_names ?? [],
});

/* ---------- queries ---------- */

/** What the signed-in person may do in this account. Refetches on focus and, with `useAccessLive` mounted, on the live event. */
export const useAccess = () => {
  const ws = useWorkspaceId();
  const q = useQuery({
    queryKey: scopedKey(ws, "access"),
    queryFn: async () => mapAccess((await api.get<{ data: Wire }>("/api/auth/next/me/access")).data),
    staleTime: 30_000,
    enabled: ws !== null,
  });
  return { data: q.data, isLoading: q.isLoading, error: q.error, refetch: q.refetch };
};

/**
 * True when the person holds the permission (a list: any of them, or `"all"` for every one). False while loading and
 * whenever the last answer failed, even if an older one is still held: controls appear only when allowed.
 */
export const useCan = (perm: Perm | Perm[], mode: "any" | "all" = "any"): boolean => {
  const { data, error } = useAccess();
  // A failed refresh makes the old answer untrustworthy (access may have been taken away): controls go, never stay.
  if (error || !data) return false;
  const held = data.permissions;
  if (!Array.isArray(perm)) return held.includes(perm);
  return mode === "all" ? perm.every((p) => held.includes(p)) : perm.some((p) => held.includes(p));
};

/** Names and lists every member of the account needs (categories, projects, who is on the team by name). */
export const useAccountBasics = () => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: scopedKey(ws, "account-basics"),
    queryFn: async () => mapBasics((await api.get<{ data: Wire }>("/api/auth/next/account/basics")).data),
    staleTime: 30_000,
    enabled: ws !== null,
  });
};
